import React, { useState } from 'react';
import { 
  Mail, 
  Search, 
  Filter, 
  Eye, 
  Trash2, 
  Calendar,
  User,
  MessageSquare,
  CheckCircle,
  Clock,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

interface ContactInquiry {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: 'new' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high';
  created_at: string;
  updated_at: string;
  admin_notes?: string;
  closed_at?: string | null;
}

export const ContactInquiriesManager: React.FC = () => {
  const { contactInquiries, updateContactInquiry, deleteContactInquiry } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [subjectFilter, setSubjectFilter] = useState<string>('all');
  const [selectedInquiry, setSelectedInquiry] = useState<ContactInquiry | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  const subjectCategories = [
    'general',
    'support', 
    'partnership',
    'billing',
    'feedback',
    'other'
  ];

  const getSubjectLabel = (subject: string) => {
    const labels = {
      general: 'General Inquiry',
      support: 'Technical Support',
      partnership: 'Partnership Opportunities',
      billing: 'Billing Questions',
      feedback: 'Feedback',
      other: 'Other'
    };
    return labels[subject as keyof typeof labels] || subject;
  };

  const getStatusColor = (status: string) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status: string) => {
    const icons = {
      new: <AlertCircle size={16} />,
      in_progress: <Clock size={16} />,
      resolved: <CheckCircle size={16} />,
      closed: <CheckCircle size={16} />
    };
    return icons[status as keyof typeof icons] || <AlertCircle size={16} />;
  };

  const getPriorityColor = (priority: string) => {
    const colors = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-red-100 text-red-800'
    };
    return colors[priority as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const filteredInquiries = (contactInquiries || []).filter((inquiry: ContactInquiry) => {
    const matchesSearch = 
      inquiry.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inquiry.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inquiry.message?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || inquiry.status === statusFilter;
    const matchesSubject = subjectFilter === 'all' || inquiry.subject === subjectFilter;
    
    return matchesSearch && matchesStatus && matchesSubject;
  });

  const handleViewDetails = (inquiry: ContactInquiry) => {
    setSelectedInquiry(inquiry);
    setAdminNotes(inquiry.admin_notes || '');
    setShowDetailsModal(true);
  };

  const handleUpdateStatus = (inquiry: ContactInquiry, newStatus: string) => {
    const updates: Partial<ContactInquiry> = {
      status: newStatus as ContactInquiry['status'],
      updated_at: new Date().toISOString()
    };

    // When moving to Resolved or Closed from a non-closed state, set closed_at timestamp once
    if ((newStatus === 'resolved' || newStatus === 'closed') && !inquiry.closed_at) {
      updates.closed_at = new Date().toISOString();
    }

    updateContactInquiry(inquiry.id, updates);
  };

  const handleUpdatePriority = (inquiryId: string, newPriority: string) => {
    updateContactInquiry(inquiryId, { 
      priority: newPriority as ContactInquiry['priority'],
      updated_at: new Date().toISOString()
    });
  };

  const handleSaveNotes = () => {
    if (selectedInquiry) {
      updateContactInquiry(selectedInquiry.id, {
        admin_notes: adminNotes,
        updated_at: new Date().toISOString()
      });
      setShowDetailsModal(false);
    }
  };

  const getInquiryStats = () => {
    const total = contactInquiries?.length || 0;
    const newCount = contactInquiries?.filter((i: ContactInquiry) => i.status === 'new').length || 0;
    const inProgress = contactInquiries?.filter((i: ContactInquiry) => i.status === 'in_progress').length || 0;
    const resolved = contactInquiries?.filter((i: ContactInquiry) => i.status === 'resolved').length || 0;
    
    return { total, newCount, inProgress, resolved };
  };

  const stats = getInquiryStats();

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Inquiries</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{stats.total}</p>
            </div>
            <div className="p-3 rounded-full bg-blue-100">
              <Mail className="text-blue-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">New</p>
              <p className="text-2xl font-bold text-blue-600 mt-2">{stats.newCount}</p>
            </div>
            <div className="p-3 rounded-full bg-blue-100">
              <AlertCircle className="text-blue-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">In Progress</p>
              <p className="text-2xl font-bold text-yellow-600 mt-2">{stats.inProgress}</p>
            </div>
            <div className="p-3 rounded-full bg-yellow-100">
              <Clock className="text-yellow-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Resolved</p>
              <p className="text-2xl font-bold text-green-600 mt-2">{stats.resolved}</p>
            </div>
            <div className="p-3 rounded-full bg-green-100">
              <CheckCircle className="text-green-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search inquiries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          <div className="relative">
            <MessageSquare className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <select
              value={subjectFilter}
              onChange={(e) => setSubjectFilter(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
            >
              <option value="all">All Subjects</option>
              {subjectCategories.map(subject => (
                <option key={subject} value={subject}>{getSubjectLabel(subject)}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Inquiries Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subject
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Closed
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredInquiries.map((inquiry: ContactInquiry) => (
                <tr key={inquiry.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="bg-blue-100 p-2 rounded-lg mr-3">
                        <User className="text-blue-600" size={16} />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{inquiry.name}</div>
                        <div className="text-sm text-gray-500">{inquiry.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{getSubjectLabel(inquiry.subject)}</div>
                    <div className="text-sm text-gray-500 truncate max-w-xs">
                      {inquiry.message.substring(0, 100)}...
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <select
                      value={inquiry.status}
                      onChange={(e) => handleUpdateStatus(inquiry, e.target.value)}
                      className={`px-2 py-1 text-xs font-medium rounded-full border-0 ${getStatusColor(inquiry.status)}`}
                    >
                      <option value="new">New</option>
                      <option value="in_progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  </td>
                  <td className="px-6 py-4">
                    <select
                      value={inquiry.priority}
                      onChange={(e) => handleUpdatePriority(inquiry.id, e.target.value)}
                      className={`px-2 py-1 text-xs font-medium rounded-full border-0 ${getPriorityColor(inquiry.priority)}`}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <div className="flex items-start flex-col">
                      <div className="flex items-center">
                        <Calendar size={14} className="mr-1" />
                        {/* Date: dd/mm/yyyy */}
                        {new Date(inquiry.created_at).toLocaleDateString('en-GB')}
                      </div>
                      <div className="ml-5 text-xs text-gray-400">
                        {/* Time: HH:MM */}
                        {new Date(inquiry.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <div className="flex items-start flex-col">
                      {inquiry.closed_at ? (
                        <>
                          <div className="flex items-center">
                            <Calendar size={14} className="mr-1" />
                            {/* Date: dd/mm/yyyy */}
                            {new Date(inquiry.closed_at).toLocaleDateString('en-GB')}
                          </div>
                          <div className="ml-5 text-xs text-gray-400">
                            {/* Time: HH:MM */}
                            {new Date(inquiry.closed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </>
                      ) : (
                        <span className="text-gray-400 text-xs italic">—</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewDetails(inquiry)}
                        className="text-blue-600 hover:text-blue-800 transition duration-200"
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => deleteContactInquiry(inquiry.id)}
                        className="text-red-600 hover:text-red-800 transition duration-200"
                        title="Delete Inquiry"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredInquiries.length === 0 && (
          <div className="text-center py-12">
            <Mail className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No inquiries found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter !== 'all' || subjectFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'No contact inquiries have been submitted yet.'
              }
            </p>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedInquiry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Inquiry Details</h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-6">
                {/* Contact Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Contact Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Name</label>
                      <p className="text-sm text-gray-900">{selectedInquiry.name}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Email</label>
                      <p className="text-sm text-gray-900">{selectedInquiry.email}</p>
                    </div>
                  </div>
                </div>

                {/* Inquiry Details */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Inquiry Details</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Subject</label>
                      <p className="text-sm text-gray-900">{getSubjectLabel(selectedInquiry.subject)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Message</label>
                      <p className="text-sm text-gray-900 whitespace-pre-wrap bg-gray-50 p-3 rounded-lg">
                        {selectedInquiry.message}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Status & Priority */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Status</label>
                    <div className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(selectedInquiry.status)} mt-1`}>
                      {getStatusIcon(selectedInquiry.status)}
                      <span className="ml-1 capitalize">{selectedInquiry.status.replace('_', ' ')}</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Priority</label>
                    <div className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(selectedInquiry.priority)} mt-1`}>
                      <span className="capitalize">{selectedInquiry.priority}</span>
                    </div>
                  </div>
                </div>

                {/* Admin Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Admin Notes</label>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Add internal notes about this inquiry..."
                  />
                </div>

                {/* Timestamps */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Timeline</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <label className="font-medium text-gray-500">Created</label>
                      <p className="text-gray-900">{new Date(selectedInquiry.created_at).toLocaleString()}</p>
                    </div>
                    <div>
                      <label className="font-medium text-gray-500">Last Updated</label>
                      <p className="text-gray-900">{new Date(selectedInquiry.updated_at).toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveNotes}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition duration-200"
                >
                  Save Notes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
